import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Syringe, Stethoscope, Droplets, Heart, DollarSign, Clock, Home } from "lucide-react";

const ICONS = { syringe: Syringe, stethoscope: Stethoscope, droplets: Droplets, heart: Heart };
const ICON_OPTIONS = ["syringe", "stethoscope", "droplets", "heart"];
const CATEGORIES = ["Terapias Injetáveis", "Consultas", "Exames", "Outros"];

const empty = { name: "", category: CATEGORIES[0], duration_minutes: 30, price: 0, description: "", benefits: [], indications: [], icon: "stethoscope", display_order: 999, active: true, show_on_home: false };

export default function AdminServicesPage() {
  const [services, setServices] = useState([]);
  const [editing, setEditing] = useState(null); // service or {} for new
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/services");
      setServices(data);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Falha ao carregar dados."); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => setEditing({ ...empty });
  const openEdit = (s) => setEditing({
    ...s,
    benefits: (s.benefits || []).join("\n"),
    indications: (s.indications || []).join("\n"),
  });
  const close = () => setEditing(null);

  const save = async () => {
    if (!editing.name || !editing.category || !editing.duration_minutes || editing.price < 0) {
      toast.error("Preencha nome, categoria, duração e preço corretamente.");
      return;
    }
    setSaving(true);
    try {
      const linesToArray = (s) => String(s || "").split("\n").map((x) => x.trim()).filter(Boolean);
      const benefits = Array.isArray(editing.benefits) ? editing.benefits : linesToArray(editing.benefits);
      const indications = Array.isArray(editing.indications) ? editing.indications : linesToArray(editing.indications);
      if (editing.id) {
        await api.patch(`/admin/services/${editing.id}`, {
          name: editing.name, category: editing.category,
          duration_minutes: Number(editing.duration_minutes),
          price: Number(editing.price), description: editing.description,
          benefits, indications,
          icon: editing.icon, active: editing.active,
          display_order: Number(editing.display_order ?? 0),
          show_on_home: !!editing.show_on_home,
        });
        toast.success("Serviço atualizado.");
      } else {
        await api.post("/admin/services", {
          name: editing.name, category: editing.category,
          duration_minutes: Number(editing.duration_minutes),
          price: Number(editing.price), description: editing.description,
          benefits, indications,
          icon: editing.icon,
          display_order: Number(editing.display_order ?? 0),
          show_on_home: !!editing.show_on_home,
        });
        toast.success("Serviço criado.");
      }
      close();
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao salvar.");
    } finally { setSaving(false); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Desativar o serviço "${s.name}"? Ele deixará de aparecer no catálogo do paciente.`)) return;
    try {
      await api.delete(`/admin/services/${s.id}`);
      toast.success("Serviço desativado.");
      await load();
    } catch (_) { toast.error("Erro ao desativar."); }
  };

  return (
    <div className="space-y-6" data-testid="admin-services-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline text-gold mb-3">Catálogo</div>
          <h1 className="brand-wordmark text-3xl md:text-4xl font-bold tracking-tight uppercase text-primary">Serviços</h1>
          <p className="text-muted-foreground mt-2">Edite preços, tempos, descrições. Novos serviços aparecem imediatamente para os pacientes.</p>
        </div>
        <button
          onClick={openNew}
          className="inline-flex items-center gap-2 rounded-sm bg-primary text-primary-foreground px-4 py-2.5 text-sm font-semibold hover:bg-primary/90 transition-colors"
          data-testid="new-service-btn"
        >
          <Plus className="w-4 h-4" /> Novo serviço
        </button>
      </div>

      <div className="bg-card border border-border rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-border text-left">
            <tr>
              <th className="px-4 py-3 overline text-muted-foreground">Serviço</th>
              <th className="px-4 py-3 overline text-muted-foreground">Categoria</th>
              <th className="px-4 py-3 overline text-muted-foreground">Duração</th>
              <th className="px-4 py-3 overline text-muted-foreground">Preço</th>
              <th className="px-4 py-3 overline text-muted-foreground">Ordem</th>
              <th className="px-4 py-3 overline text-muted-foreground">Home</th>
              <th className="px-4 py-3 overline text-muted-foreground">Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {services.map((s) => {
              const Icon = ICONS[s.icon] || Stethoscope;
              return (
                <tr key={s.id} className="border-b border-border last:border-0 hover:bg-muted/40 transition-colors" data-testid={`admin-service-row-${s.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-sm bg-accent/10 text-accent grid place-items-center flex-none">
                        <Icon className="w-4 h-4" />
                      </div>
                      <div>
                        <div className="font-medium">{s.name}</div>
                        {s.description && <div className="text-xs text-muted-foreground line-clamp-1 max-w-md">{s.description}</div>}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{s.category}</td>
                  <td className="px-4 py-3">{s.duration_minutes} min</td>
                  <td className="px-4 py-3 font-semibold text-primary">R$ {Number(s.price).toFixed(0)}</td>
                  <td className="px-4 py-3 text-muted-foreground text-xs" data-testid={`service-order-${s.id}`}>{s.display_order ?? 0}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center gap-1 overline px-2 py-0.5 rounded-sm border ${
                        s.show_on_home ? "border-gold/40 bg-gold/10 text-gold" : "border-border bg-muted/30 text-muted-foreground"
                      }`}
                      data-testid={`service-home-badge-${s.id}`}
                    >
                      <Home className="w-3 h-3" /> {s.show_on_home ? "Sim" : "Não"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-block overline px-2 py-0.5 rounded-sm border ${s.active ? "border-success/40 text-success bg-success/5" : "border-border text-muted-foreground bg-muted"}`}>
                      {s.active ? "Ativo" : "Inativo"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-2">
                      <button onClick={() => openEdit(s)} className="p-2 rounded-sm hover:bg-muted transition-colors" data-testid={`edit-service-${s.id}`} title="Editar">
                        <Pencil className="w-4 h-4 text-primary" />
                      </button>
                      {s.active && (
                        <button onClick={() => remove(s)} className="p-2 rounded-sm hover:bg-muted transition-colors" data-testid={`delete-service-${s.id}`} title="Desativar">
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {services.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-12 text-center text-muted-foreground">Nenhum serviço cadastrado.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Edit/Create modal */}
      {editing && (
        <div className="fixed inset-0 bg-black/50 z-50 grid place-items-center p-4" onClick={close} data-testid="service-modal">
          <div className="bg-card border border-border rounded-sm p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="overline text-gold mb-1">{editing.id ? "Editar serviço" : "Novo serviço"}</div>
                <h3 className="brand-wordmark uppercase text-xl font-bold text-primary">{editing.id ? editing.name : "Cadastrar"}</h3>
              </div>
              <button onClick={close} className="p-1 hover:bg-muted rounded-sm" data-testid="close-service-modal">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="overline text-muted-foreground mb-1 block">Nome</label>
                <input
                  value={editing.name}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  data-testid="service-name-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="overline text-muted-foreground mb-1 block">Categoria</label>
                  <select
                    value={editing.category}
                    onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                    className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm"
                    data-testid="service-category-select"
                  >
                    {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="overline text-muted-foreground mb-1 block">Ícone</label>
                  <select
                    value={editing.icon}
                    onChange={(e) => setEditing({ ...editing, icon: e.target.value })}
                    className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm"
                    data-testid="service-icon-select"
                  >
                    {ICON_OPTIONS.map((i) => <option key={i} value={i}>{i}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="overline text-muted-foreground mb-1 block flex items-center gap-1"><Clock className="w-3 h-3" /> Duração (min)</label>
                  <input
                    type="number" min={1} max={480}
                    value={editing.duration_minutes}
                    onChange={(e) => setEditing({ ...editing, duration_minutes: e.target.value })}
                    className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm"
                    data-testid="service-duration-input"
                  />
                </div>
                <div>
                  <label className="overline text-muted-foreground mb-1 block flex items-center gap-1"><DollarSign className="w-3 h-3" /> Preço (R$)</label>
                  <input
                    type="number" step="0.01" min={0}
                    value={editing.price}
                    onChange={(e) => setEditing({ ...editing, price: e.target.value })}
                    className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm"
                    data-testid="service-price-input"
                  />
                </div>
              </div>
              <div>
                <label className="overline text-muted-foreground mb-1 block">Ordem de exibição (menor = primeiro)</label>
                <input
                  type="number" min={0} max={9999}
                  value={editing.display_order ?? 999}
                  onChange={(e) => setEditing({ ...editing, display_order: e.target.value })}
                  className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm"
                  data-testid="service-order-input"
                />
                <div className="text-xs text-muted-foreground mt-1">Ex.: 1 aparece primeiro, 2 segundo… Sem prioridade? Deixe 999.</div>
              </div>
              <div>
                <label className="overline text-muted-foreground mb-1 block">Descrição</label>
                <textarea
                  rows={3}
                  value={editing.description}
                  onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                  className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm"
                  placeholder="Texto completo explicando do que se trata o serviço."
                  data-testid="service-description-input"
                />
              </div>
              <div>
                <label className="overline text-muted-foreground mb-1 block">Benefícios (um por linha)</label>
                <textarea
                  rows={4}
                  value={typeof editing.benefits === "string" ? editing.benefits : (editing.benefits || []).join("\n")}
                  onChange={(e) => setEditing({ ...editing, benefits: e.target.value })}
                  className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm"
                  placeholder="Ex:\nMelhora dos níveis de energia\nApoio ao sistema imunológico\nRedução da fadiga metabólica"
                  data-testid="service-benefits-input"
                />
                <div className="text-xs text-muted-foreground mt-1">Cada linha vira um item da lista exibida no modal do paciente.</div>
              </div>
              <div>
                <label className="overline text-muted-foreground mb-1 block">Indicações / para quem é (um por linha)</label>
                <textarea
                  rows={3}
                  value={typeof editing.indications === "string" ? editing.indications : (editing.indications || []).join("\n")}
                  onChange={(e) => setEditing({ ...editing, indications: e.target.value })}
                  className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm"
                  placeholder="Ex:\nAdultos com deficiência confirmada\nPacientes em tratamento clínico"
                  data-testid="service-indications-input"
                />
              </div>
              <label className="flex items-start gap-3 p-3 rounded-sm border border-border bg-muted/30">
                <input
                  type="checkbox"
                  checked={!!editing.show_on_home}
                  onChange={(e) => setEditing({ ...editing, show_on_home: e.target.checked })}
                  className="accent-primary w-4 h-4 mt-0.5"
                  data-testid="service-show-on-home-checkbox"
                />
                <div>
                  <div className="text-sm font-medium inline-flex items-center gap-1.5">
                    <Home className="w-3.5 h-3.5 text-gold" /> Exibir na home
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    Quando ligado, o serviço aparece na grade pública da home. Desligado, continua disponível na aba de agendamento — só some do destaque público.
                  </div>
                </div>
              </label>
              {editing.id && (
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={editing.active}
                    onChange={(e) => setEditing({ ...editing, active: e.target.checked })}
                    className="accent-primary w-4 h-4"
                    data-testid="service-active-checkbox"
                  />
                  <span className="text-sm">Serviço ativo (visível para pacientes)</span>
                </label>
              )}
              <div className="flex gap-2 justify-end pt-2">
                <button onClick={close} className="px-4 py-2 rounded-sm border border-border text-sm hover:bg-muted transition-colors" data-testid="cancel-service-btn">Cancelar</button>
                <button
                  onClick={save} disabled={saving}
                  className="px-4 py-2 rounded-sm bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 disabled:opacity-60 transition-colors"
                  data-testid="save-service-btn"
                >
                  {saving ? "Salvando…" : "Salvar"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
